/**
 * Track which vendor-portal submitted Draft bills the company user has already
 * visited on Draft Bills → Submitted. Badge counts only include unseen IDs.
 *
 * Pending IDs come from Socket.IO so the reminder can show immediately even if
 * the bills list API has not returned the new row yet.
 */

const STORAGE_PREFIX = 'draft-bills-submitted-seen-v1'
const PENDING_PREFIX = 'draft-bills-submitted-pending-v1'
export const SUBMITTED_INVOICES_SEEN_EVENT = 'submitted-invoices-seen'
/** Fired when Socket.IO reports a new vendor-portal submission (company users). */
export const VENDOR_SUBMITTED_INVOICE_EVENT = 'vendor-submitted-invoice'

function storageKey(companyId) {
  return `${STORAGE_PREFIX}:${String(companyId || '')}`
}

function pendingKey(companyId) {
  return `${PENDING_PREFIX}:${String(companyId || '')}`
}

function readIdSet(key) {
  try {
    const raw = localStorage.getItem(key)
    const list = raw ? JSON.parse(raw) : []
    if (!Array.isArray(list)) return new Set()
    return new Set(list.map((id) => String(id)).filter(Boolean))
  } catch (_) {
    return new Set()
  }
}

function writeIdSet(key, ids) {
  try {
    const unique = [...new Set([...(ids || [])].map((id) => String(id)).filter(Boolean))]
    localStorage.setItem(key, JSON.stringify(unique))
  } catch (_) {
    // ignore storage errors
  }
}

export function readSeenSubmittedInvoiceIds(companyId) {
  if (companyId == null || companyId === '') return new Set()
  return readIdSet(storageKey(companyId))
}

export function writeSeenSubmittedInvoiceIds(companyId, ids) {
  if (companyId == null || companyId === '') return
  writeIdSet(storageKey(companyId), ids)
}

export function readPendingSubmittedInvoiceIds(companyId) {
  if (companyId == null || companyId === '') return new Set()
  return readIdSet(pendingKey(companyId))
}

export function writePendingSubmittedInvoiceIds(companyId, ids) {
  if (companyId == null || companyId === '') return
  writeIdSet(pendingKey(companyId), ids)
}

/** Remember a socket-reported submission until the bills list (or Seen) catches up. */
export function rememberPendingSubmittedInvoice(companyId, billId) {
  if (companyId == null || companyId === '' || billId == null || billId === '') return
  const id = String(billId)
  const seen = readSeenSubmittedInvoiceIds(companyId)
  if (seen.has(id)) return
  const pending = readPendingSubmittedInvoiceIds(companyId)
  if (pending.has(id)) return
  pending.add(id)
  writePendingSubmittedInvoiceIds(companyId, pending)
}

/** Drop pending IDs that are already seen. */
export function prunePendingSubmittedInvoices(companyId) {
  if (companyId == null || companyId === '') return
  const seen = readSeenSubmittedInvoiceIds(companyId)
  const pending = readPendingSubmittedInvoiceIds(companyId)
  let changed = false
  ;[...pending].forEach((id) => {
    if (seen.has(id)) {
      pending.delete(id)
      changed = true
    }
  })
  if (changed) writePendingSubmittedInvoiceIds(companyId, pending)
}

export function clearPendingSubmittedInvoices(companyId, billIds) {
  if (companyId == null || companyId === '') return
  const ids = new Set([...(billIds || [])].map((id) => String(id)).filter(Boolean))
  if (!ids.size) return
  const pending = readPendingSubmittedInvoiceIds(companyId)
  let changed = false
  ids.forEach((id) => {
    if (pending.delete(id)) changed = true
  })
  if (changed) writePendingSubmittedInvoiceIds(companyId, pending)
}

/** Merge bill IDs into the seen set and notify listeners (e.g. FAB badge). */
export function markSubmittedInvoicesSeen(companyId, billIds) {
  if (companyId == null || companyId === '') return []
  const ids = [...new Set([...(billIds || [])].map((id) => String(id)).filter(Boolean))]
  if (!ids.length) return [...readSeenSubmittedInvoiceIds(companyId)]
  const next = readSeenSubmittedInvoiceIds(companyId)
  ids.forEach((id) => next.add(id))
  writeSeenSubmittedInvoiceIds(companyId, next)
  clearPendingSubmittedInvoices(companyId, ids)
  try {
    window.dispatchEvent(new CustomEvent(SUBMITTED_INVOICES_SEEN_EVENT, {
      detail: { companyId: String(companyId), billIds: ids }
    }))
  } catch (_) {
    // ignore
  }
  return [...next]
}

export function countUnseenSubmittedInvoices(companyId, awaitingRecords) {
  const list = Array.isArray(awaitingRecords) ? awaitingRecords : []
  const seen = readSeenSubmittedInvoiceIds(companyId)
  const unseen = new Set()
  list.forEach((row) => {
    const id = row?.id != null ? String(row.id) : ''
    if (id && !seen.has(id)) unseen.add(id)
  })
  readPendingSubmittedInvoiceIds(companyId).forEach((id) => {
    if (id && !seen.has(id)) unseen.add(id)
  })
  // Drop pending that the user already marked seen
  prunePendingSubmittedInvoices(companyId)
  return unseen.size
}
